import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiPackage, FiUser, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { usersAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import './ChangePlan.css';

const ChangePlan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { partner } = useContext(AuthContext);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const planOptions = [
    { value: 0, name: 'Alpha', description: 'Basic access with limited features', color: '#e3f2fd' },
    { value: 1, name: 'Beta', description: 'Standard access with core features', color: '#f3e5f5' },
    { value: 2, name: 'Gamma', description: 'Enhanced access with advanced features', color: '#e8f5e8' },
    { value: 3, name: 'Delta', description: 'Premium access with full features', color: '#fff3e0' },
    { value: 4, name: 'Epsilon', description: 'Enterprise access with all features', color: '#fce4ec' },
    { value: 5, name: 'Zeta', description: 'Ultimate access with maximum features', color: '#f1f8e9' }
  ];

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await usersAPI.getById(id);
      
      if (response.success) {
        const userData = response.user;
        setUser(userData);
        setSelectedPlan(userData.plan !== undefined ? userData.plan : 0);
      } else {
        setError(response.message || 'Failed to load user details');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setError('Failed to load user details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (planValue) => {
    setSelectedPlan(planValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedPlan === null) {
      toast.error('Please select a plan');
      return;
    }

    try {
      setSaving(true);
      
      const response = await usersAPI.updatePlan(id, selectedPlan);
      
      if (response.success) {
        toast.success(`User plan updated to ${planOptions[selectedPlan].name}`);
        navigate(`/users/${id}`);
      } else {
        toast.error(response.message || 'Failed to update user plan');
      }
    } catch (error) {
      console.error('Error updating user plan:', error);
      toast.error('Failed to update user plan. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/users/${id}`);
  };

  const getCurrentPlanName = (plan) => {
    if (plan === undefined || plan === null) return 'N/A';
    return planOptions[plan]?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="change-plan-page">
        <div className="headerSection">
          <div className="header-content">
            <div className="header-left">
              <h2>Change User Plan</h2>
              <p>Loading user information...</p>
            </div>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="dataArea-content">
          <div className="loading">Loading user details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="change-plan-page">
        <div className="headerSection">
          <div className="header-content">
            <div className="header-left">
              <h2>Change User Plan</h2>
              <p>Error loading user information</p>
            </div>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="dataArea-content">
          <div className="error-message">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchUserDetails}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="change-plan-page">
        <div className="headerSection">
          <div className="header-content">
            <div className="header-left">
              <h2>Change User Plan</h2>
              <p>User not found</p>
            </div>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="dataArea-content">
          <div className="error-message">
            <p>User not found</p>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="change-plan-page">
      <div className="headerSection">
        <div className="header-content">
          <div className="header-left">
            <h2>Change User Plan</h2>
            <p>Update user's plan level</p>
          </div>
          <button className="back-btn" onClick={() => navigate(`/users/${id}`)}>
            <FiArrowLeft />
            Back to User Details
          </button>
        </div>
      </div>

      <div className="dataArea-content">
        <div className="change-plan-container">
          <div className="user-info-section">
            <div className="user-avatar">
              <FiUser />
            </div>
            <div className="user-details">
              <h3>{user.name || 'N/A'}</h3>
              <p>{user.email}</p>
              <div className="current-plan">
                <span className="plan-label">Current Plan:</span>
                <span className="plan-badge current">
                  {getCurrentPlanName(user.plan)}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="plan-selection-form">
            <div className="form-section">
              <h3>Select New Plan</h3>
              <p className="section-description">
                Choose a plan level for this user. Higher plan levels provide more features and access.
              </p>
              
              <div className="plan-options">
                {planOptions.map((plan) => (
                  <div
                    key={plan.value}
                    className={`plan-option ${selectedPlan === plan.value ? 'selected' : ''}`}
                    onClick={() => handlePlanSelect(plan.value)}
                  >
                    <div className="plan-header">
                      <div className="plan-name">
                        <h4>{plan.name}</h4>
                        <span className="plan-level">Level {plan.value}</span>
                      </div>
                      {selectedPlan === plan.value && (
                        <div className="selected-indicator">
                          <FiCheck />
                        </div>
                      )}
                    </div>
                    <p className="plan-description">{plan.description}</p>
                    <div 
                      className="plan-color-indicator"
                      style={{ backgroundColor: plan.color }}
                    ></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-section">
              <h3>Plan Change Summary</h3>
              
              <div className="summary-item">
                <label>User:</label>
                <span>{user.name || 'N/A'} ({user.email})</span>
              </div>
              
              <div className="summary-item">
                <label>Current Plan:</label>
                <span className="plan-badge current">
                  {getCurrentPlanName(user.plan)}
                </span>
              </div>
              
              <div className="summary-item">
                <label>New Plan:</label>
                <span className="plan-badge new">
                  {selectedPlan !== null ? planOptions[selectedPlan].name : 'Not selected'}
                </span>
              </div>
              
              <div className="summary-item">
                <label>Extension:</label>
                <span className="extension-badge">
                  {user.extensionId?.name || user.extensionName || 'N/A'}
                </span>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || selectedPlan === null}
              >
                {saving ? (
                  <>
                    <div className="spinner"></div>
                    Updating Plan...
                  </>
                ) : (
                  <>
                    <FiSave />
                    Update Plan
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePlan; 